import { z } from "zod";
import { listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { entityTypeSchema } from "./common.ts";
import { integrationConnectionSchema } from "./integrations.ts";

/**
 * COMMUNICATION & PRODUCTIVITY INTEGRATIONS (PRD §9 Item 16: US-16.4 WhatsApp,
 * US-16.5 Slack, US-16.6 Google Drive, US-16.7 Zapier / Make / n8n; F9 webhooks).
 *
 * Built on the integrations FOUNDATION in ./integrations.ts: every connection
 * here is an `IntegrationConnectionDto` (credentials go IN on the connect
 * inputs and never come back OUT), every call we make to a provider is an
 * outbound delivery, every webhook a provider sends us lands in the inbox.
 *
 * External contracts (the REST-hook event catalogue and its payloads) are
 * versioned (`invoice.paid.v1`) and deliberately separate from the internal
 * outbox event names, so an internal refactor never breaks a customer's Zap
 * (principles §9.1).
 */

// ---------------------------------------------------------------------------
// OAuth (Slack, Google) — the state parameter is signed, org- and user-bound
// ---------------------------------------------------------------------------

/** POST /api/v1/integrations/{slack|google-drive}/oauth/start (perm `integrations:manage`). 503 when the platform app is not configured. */
export const commsOAuthStartSchema = z.object({
  /** Where to send the browser. Carries a signed, 10-minute, org- and user-bound `state`. */
  url: z.string(),
  /** The redirect URI registered with the provider — the web app page that finishes the flow. */
  redirectUri: z.string(),
  expiresAt: timestampSchema,
});
export type CommsOAuthStartDto = z.infer<typeof commsOAuthStartSchema>;

/** POST /api/v1/integrations/{slack|google-drive}/oauth/complete (perm `integrations:manage`) → 201. Bad/expired/foreign state → 422. */
export const commsOAuthCompleteInputSchema = z
  .object({
    code: z.string().trim().min(1).max(2000),
    state: z.string().trim().min(1).max(2000),
  })
  .strict();
export type CommsOAuthCompleteInput = z.input<typeof commsOAuthCompleteInputSchema>;

/** A connection or `null` — GET /api/v1/integrations/{slack|google-drive|whatsapp}. */
export const commsConnectionEnvelopeSchema = z.object({ connection: integrationConnectionSchema.nullable() });
export type CommsConnectionEnvelopeDto = z.infer<typeof commsConnectionEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Slack
// ---------------------------------------------------------------------------

/** Events a workspace may route to a Slack channel (US-16.5, integrations-reference §6.4). */
export const SLACK_ROUTABLE_EVENTS = [
  "lead.created",
  "lead.won",
  "lead.lost",
  "client.created",
  "proposal.accepted",
  "contract.signed",
  "invoice.issued",
  "invoice.paid",
  "invoice.overdue",
  "payment.recorded",
  "project.health_changed",
  "project.completed",
  "task.completed",
  "deliverable.approved",
  "deliverable.changes_requested",
  "integration.failed",
] as const;
export type SlackRoutableEvent = (typeof SLACK_ROUTABLE_EVENTS)[number];
export const slackRoutableEventSchema = z.enum(SLACK_ROUTABLE_EVENTS);

export const SLACK_EVENT_LABELS: Record<SlackRoutableEvent, string> = {
  "lead.created": "New lead",
  "lead.won": "Lead won",
  "lead.lost": "Lead lost",
  "client.created": "New client",
  "proposal.accepted": "Proposal accepted",
  "contract.signed": "Contract signed",
  "invoice.issued": "Invoice issued",
  "invoice.paid": "Invoice paid",
  "invoice.overdue": "Invoice overdue",
  "payment.recorded": "Payment received",
  "project.health_changed": "Project health changed",
  "project.completed": "Project completed",
  "task.completed": "Task completed",
  "deliverable.approved": "Creative approved",
  "deliverable.changes_requested": "Creative changes requested",
  "integration.failed": "Integration failure",
};

/** POST /api/v1/integrations/slack/webhook (perm `integrations:manage`) → 201 connection. Only `https://hooks.slack.com/…` is accepted. */
export const connectSlackWebhookInputSchema = z
  .object({
    webhookUrl: z.string().trim().url().max(500),
    /** Shown in the hub only; an incoming webhook cannot change its channel. */
    channelName: z.string().trim().max(80).optional(),
  })
  .strict();
export type ConnectSlackWebhookInput = z.input<typeof connectSlackWebhookInputSchema>;

/** One routing rule: this event → this channel. */
export const slackRouteSchema = z.object({
  id: uuidSchema,
  eventType: slackRoutableEventSchema,
  /** Slack channel id (C…). `null` = the connection's default channel. */
  channelId: z.string().nullable(),
  channelName: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type SlackRouteDto = z.infer<typeof slackRouteSchema>;

/** PUT /api/v1/integrations/slack/routes (perm `integrations:manage`) → the new list. Replaced as a whole. */
export const putSlackRoutesInputSchema = z
  .object({
    routes: z
      .array(
        z
          .object({
            eventType: slackRoutableEventSchema,
            channelId: z.string().trim().regex(/^[CGD][A-Z0-9]{2,30}$/, "Use a Slack channel id such as C0123ABCD").nullable().optional(),
            channelName: z.string().trim().max(80).nullable().optional(),
            enabled: z.boolean().optional(),
          })
          .strict(),
      )
      .max(100),
  })
  .strict();
export type PutSlackRoutesInput = z.input<typeof putSlackRoutesInputSchema>;

/** POST /api/v1/integrations/slack/test (perm `integrations:manage`) → 202. */
export const slackTestInputSchema = z
  .object({ channelId: z.string().trim().regex(/^[CGD][A-Z0-9]{2,30}$/).optional() })
  .strict();
export type SlackTestInput = z.input<typeof slackTestInputSchema>;

/** A queued outbound call (Slack post, Drive folder, WhatsApp verification…) — 202 responses. */
export const commsQueuedSchema = z.object({
  deliveryId: uuidSchema.nullable(),
  status: z.enum(["queued", "exists", "skipped"]),
  reason: z.string().nullable(),
});
export type CommsQueuedDto = z.infer<typeof commsQueuedSchema>;

// ---------------------------------------------------------------------------
// REST hooks — Zapier, Make, n8n (F9, US-16.7, integrations-reference §7)
// ---------------------------------------------------------------------------

/** The EXTERNAL event catalogue. Names and payload schemas are versioned and stable. */
export const HOOK_EVENTS = [
  "lead.created",
  "lead.stage_changed",
  "lead.won",
  "lead.lost",
  "client.created",
  "client.status_changed",
  "proposal.signed",
  "contract.signed",
  "invoice.issued",
  "invoice.paid",
  "invoice.overdue",
  "payment.received",
  "task.completed",
  "approval.requested",
  "approval.decided",
] as const;
export type HookEvent = (typeof HOOK_EVENTS)[number];
export const hookEventSchema = z.enum(HOOK_EVENTS);

export const HOOK_SOURCES = ["zapier", "make", "n8n", "api"] as const;
export type HookSource = (typeof HOOK_SOURCES)[number];

/**
 * POST /api/v1/hooks (REST-hook `performSubscribe`; auth: an org API key
 * `Authorization: Bearer l2a_sk_…` or a session) → 201 with the signing secret,
 * shown ONCE. The caller must be able to read the event's records. Non-https,
 * private, loopback, link-local and metadata targets → 422.
 * Zapier sends `target_url`; `targetUrl` is accepted too.
 */
export const createHookInputSchema = z
  .object({
    target_url: z.string().trim().max(2000).optional(),
    targetUrl: z.string().trim().max(2000).optional(),
    event: hookEventSchema,
    source: z.enum(HOOK_SOURCES).optional(),
  })
  .strict()
  .refine((o) => Boolean(o.target_url ?? o.targetUrl), { message: "target_url is required", path: ["target_url"] });
export type CreateHookInput = z.input<typeof createHookInputSchema>;

export const hookSubscriptionSchema = z.object({
  id: uuidSchema,
  event: hookEventSchema,
  /** `invoice.paid.v1` — the payload schema this subscriber receives. */
  schema: z.string(),
  targetUrl: z.string(),
  source: z.enum(HOOK_SOURCES),
  status: z.enum(["active", "disabled"]),
  disabledReason: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  lastDeliveryAt: timestampSchema.nullable(),
  lastSuccessAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type HookSubscriptionDto = z.infer<typeof hookSubscriptionSchema>;

/** The 201 of POST /hooks: `secret` verifies `X-L2A-Signature: sha256=<hex HMAC-SHA256(secret, raw body)>`. */
export const createHookResultSchema = hookSubscriptionSchema.extend({ secret: z.string() });
export type CreateHookResultDto = z.infer<typeof createHookResultSchema>;

/** GET /api/v1/hooks/deliveries (perm `integrations:manage`). */
export const listHookDeliveriesQuerySchema = listQuerySchema.extend({
  subscriptionId: uuidSchema.optional(),
  status: z.enum(["pending", "attempting", "succeeded", "failed", "ambiguous"]).optional(),
});
export type ListHookDeliveriesQuery = z.infer<typeof listHookDeliveriesQuerySchema>;

export const hookDeliverySchema = z.object({
  id: uuidSchema,
  subscriptionId: uuidSchema.nullable(),
  event: z.string(),
  /** The envelope id — stable across retries, so receivers can dedupe. */
  eventId: z.string(),
  status: z.enum(["pending", "attempting", "succeeded", "failed", "ambiguous"]),
  attempts: z.number().int(),
  httpStatus: z.number().int().nullable(),
  lastError: z.string().nullable(),
  nextRetryAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  completedAt: timestampSchema.nullable(),
});
export type HookDeliveryDto = z.infer<typeof hookDeliverySchema>;

/** One field of an external payload, for the catalogue page and Zapier's field mapper. */
export const hookFieldSchema = z.object({ key: z.string(), type: z.string(), description: z.string() });

/** GET /api/v1/hooks/events — the versioned catalogue (any authenticated caller). */
export const hookEventCatalogueSchema = z.object({
  apiVersion: z.string(),
  signatureHeader: z.string(),
  items: z.array(
    z.object({
      event: hookEventSchema,
      version: z.number().int(),
      schema: z.string(),
      label: z.string(),
      description: z.string(),
      permission: z.string(),
      fields: z.array(hookFieldSchema),
    }),
  ),
});
export type HookEventCatalogueDto = z.infer<typeof hookEventCatalogueSchema>;

/** GET /api/v1/hooks/auth-test — Zapier's connection test and label. */
export const hookAuthTestSchema = z.object({
  orgId: uuidSchema,
  orgName: z.string(),
  /** The API key's name, or the signed-in member's name. */
  label: z.string(),
});
export type HookAuthTestDto = z.infer<typeof hookAuthTestSchema>;

/** The envelope POSTed to a subscriber and returned by `performList` (principles §9.1, external). */
export const hookEnvelopeSchema = z.object({
  id: z.string(),
  type: hookEventSchema,
  schema: z.string(),
  version: z.number().int(),
  api_version: z.string(),
  occurred_at: z.string(),
  org_id: z.string(),
  data: z.record(z.string(), z.unknown()),
});
export type HookEnvelopeDto = z.infer<typeof hookEnvelopeSchema>;

export const hookEventParamsSchema = z.object({ event: hookEventSchema });

// ---------------------------------------------------------------------------
// Google Drive
// ---------------------------------------------------------------------------

/** PATCH /api/v1/integrations/google-drive/settings (perm `integrations:manage`). Merge patch; `subfolders` replaced as a whole. */
export const updateDriveSettingsInputSchema = z
  .object({
    rootFolderId: z.string().trim().min(5).max(200).nullable(),
    rootFolderName: z.string().trim().max(200).nullable(),
    autoCreateClientFolders: z.boolean(),
    subfolders: z.array(z.string().trim().min(1).max(80)).max(20),
  })
  .partial()
  .strict();
export type UpdateDriveSettingsInput = z.input<typeof updateDriveSettingsInputSchema>;

export const DRIVE_LINK_KINDS = ["file", "folder", "client_root", "client_subfolder"] as const;

export const driveLinkSchema = z.object({
  id: uuidSchema,
  entityType: z.string(),
  entityId: uuidSchema,
  fileId: z.string(),
  name: z.string(),
  mimeType: z.string().nullable(),
  webViewLink: z.string().nullable(),
  iconLink: z.string().nullable(),
  kind: z.enum(DRIVE_LINK_KINDS),
  linkedByUserId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type DriveLinkDto = z.infer<typeof driveLinkSchema>;

/** GET /api/v1/integrations/google-drive/links?entityType&entityId (needs read access to the record). */
export const listDriveLinksQuerySchema = z.object({ entityType: entityTypeSchema, entityId: uuidSchema });
export type ListDriveLinksQuery = z.infer<typeof listDriveLinksQuerySchema>;

/** POST /api/v1/integrations/google-drive/links (perm `files:write`) → 201. The Picker result: linked, never copied. */
export const createDriveLinkInputSchema = z
  .object({
    entityType: entityTypeSchema,
    entityId: uuidSchema,
    fileId: z.string().trim().regex(/^[A-Za-z0-9_-]{5,200}$/, "Not a Google Drive file id"),
    name: z.string().trim().min(1).max(300),
    mimeType: z.string().trim().max(200).optional(),
    webViewLink: z.string().trim().url().max(1000).optional(),
    iconLink: z.string().trim().url().max(1000).optional(),
  })
  .strict();
export type CreateDriveLinkInput = z.input<typeof createDriveLinkInputSchema>;

// ---------------------------------------------------------------------------
// WhatsApp Business Cloud API (US-16.4, integrations-reference §5)
// ---------------------------------------------------------------------------

export const WHATSAPP_TEMPLATE_CATEGORIES = ["UTILITY", "AUTHENTICATION", "MARKETING"] as const;
export type WhatsAppTemplateCategory = (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

export const WHATSAPP_TEMPLATE_STATUSES = ["draft", "pending", "approved", "rejected", "paused", "disabled"] as const;

/** What the product sends the template for; `custom` is anything an admin adds. */
export const WHATSAPP_TEMPLATE_PURPOSES = [
  "invoice_issued",
  "invoice_payment_reminder",
  "payment_received",
  "weekly_report_ready",
  "approval_requested",
  "proposal_sent",
  "portal_login_otp",
  "custom",
] as const;
export type WhatsAppTemplatePurpose = (typeof WHATSAPP_TEMPLATE_PURPOSES)[number];

/**
 * FACT SLOTS — the only things a template parameter may be filled from.
 * Amounts, invoice numbers and links come from structured records, never from
 * free text or AI output (AI spec §4.3).
 */
export const WHATSAPP_FACT_SLOTS = [
  "contact.firstName",
  "contact.name",
  "client.name",
  "org.name",
  "invoice.number",
  "invoice.numbers",
  "invoice.amountDue",
  "invoice.total",
  "invoice.dueDate",
  "invoice.link",
  "payment.amount",
  "payment.date",
  "proposal.title",
  "proposal.link",
  "approval.title",
  "approval.link",
  "report.link",
  "otp.code",
] as const;
export type WhatsAppFactSlot = (typeof WHATSAPP_FACT_SLOTS)[number];
export const whatsappFactSlotSchema = z.enum(WHATSAPP_FACT_SLOTS);

/** POST /api/v1/integrations/whatsapp/connect (perm `integrations:manage`) → 201. The agency's own number and permanent token. */
export const connectWhatsAppInputSchema = z
  .object({
    phoneNumberId: z.string().trim().regex(/^\d{5,30}$/, "Use the numeric phone number id from Meta"),
    wabaId: z.string().trim().regex(/^\d{5,30}$/, "Use the numeric WhatsApp Business Account id"),
    displayPhoneNumber: z.string().trim().max(30).optional(),
    /** System-user permanent access token. Stored encrypted, never returned. */
    accessToken: z.string().trim().min(20).max(1000),
    /** The Meta app secret — keys X-Hub-Signature-256. Stored encrypted, never returned. */
    appSecret: z.string().trim().min(16).max(200),
    graphApiVersion: z.string().trim().regex(/^v\d{2}\.\d$/).optional(),
    monthlyBudgetMinor: z.number().int().min(0).max(10_000_000_000).optional(),
  })
  .strict();
export type ConnectWhatsAppInput = z.input<typeof connectWhatsAppInputSchema>;

/** 201 of connect: the webhook URL and verify token to paste into Meta. The token is shown ONCE. */
export const connectWhatsAppResultSchema = z.object({
  connection: integrationConnectionSchema,
  webhookUrl: z.string(),
  verifyToken: z.string(),
});
export type ConnectWhatsAppResultDto = z.infer<typeof connectWhatsAppResultSchema>;

/** Per-message rates in thousandths of a paisa (Meta prices are fractions of a paisa), before GST. */
export const whatsappRatesSchema = z.object({
  marketingMilliMinor: z.number().int().min(0),
  utilityMilliMinor: z.number().int().min(0),
  authenticationMilliMinor: z.number().int().min(0),
  serviceMilliMinor: z.number().int().min(0),
});

/** PATCH /api/v1/integrations/whatsapp/settings (perm `integrations:manage`). Merge patch. */
export const updateWhatsAppSettingsInputSchema = z
  .object({
    monthlyBudgetMinor: z.number().int().min(0).max(10_000_000_000),
    rates: whatsappRatesSchema.partial().strict(),
    gstBps: z.number().int().min(0).max(5000),
  })
  .partial()
  .strict();
export type UpdateWhatsAppSettingsInput = z.input<typeof updateWhatsAppSettingsInputSchema>;

export const whatsappTemplateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  language: z.string(),
  category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES),
  purpose: z.enum(WHATSAPP_TEMPLATE_PURPOSES),
  body: z.string(),
  paramSlots: z.array(z.string()),
  buttonText: z.string().nullable(),
  buttonUrlBase: z.string().nullable(),
  buttonUrlSlot: z.string().nullable(),
  status: z.enum(WHATSAPP_TEMPLATE_STATUSES),
  metaTemplateId: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type WhatsAppTemplateDto = z.infer<typeof whatsappTemplateSchema>;

const templateNameSchema = z.string().trim().regex(/^[a-z][a-z0-9_]{0,511}$/, "Lower-case letters, digits and underscores");

/** POST /api/v1/integrations/whatsapp/templates (perm `integrations:manage`) → 201 draft. Placeholder count must equal `paramSlots`. */
export const createWhatsAppTemplateInputSchema = z
  .object({
    name: templateNameSchema,
    language: z.string().trim().regex(/^[a-z]{2}(_[A-Z]{2})?$/).optional(),
    category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES),
    purpose: z.enum(WHATSAPP_TEMPLATE_PURPOSES).optional(),
    body: z.string().trim().min(1).max(1024),
    paramSlots: z.array(whatsappFactSlotSchema).max(10),
    buttonText: z.string().trim().max(25).optional(),
    buttonUrlBase: z.string().trim().url().max(2000).optional(),
    buttonUrlSlot: whatsappFactSlotSchema.optional(),
    /** Register a template that is ALREADY approved in Meta (skips submission). */
    alreadyApproved: z.boolean().optional(),
  })
  .strict();
export type CreateWhatsAppTemplateInput = z.input<typeof createWhatsAppTemplateInputSchema>;

/** A template or message send target. */
export const whatsappSubjectSchema = z.object({ entityType: z.enum(["invoice", "client", "proposal", "deliverable", "project", "payment"]), entityId: uuidSchema });

/** POST /api/v1/integrations/whatsapp/messages/template (perm `messages:write`) → 201. Refused without opt-in, after STOP, or over budget (409). */
export const sendWhatsAppTemplateInputSchema = z
  .object({
    contactId: uuidSchema,
    templateId: uuidSchema,
    subject: whatsappSubjectSchema.nullable().optional(),
  })
  .strict();
export type SendWhatsAppTemplateInput = z.input<typeof sendWhatsAppTemplateInputSchema>;

/** POST /api/v1/integrations/whatsapp/messages/text (perm `messages:write`) → 201. Only inside the 24-hour window (else 409). */
export const sendWhatsAppTextInputSchema = z
  .object({
    contactId: uuidSchema,
    body: z.string().trim().min(1).max(4096),
  })
  .strict();
export type SendWhatsAppTextInput = z.input<typeof sendWhatsAppTextInputSchema>;

export const WHATSAPP_MESSAGE_STATUSES = ["queued", "sent", "delivered", "read", "failed", "unconfirmed", "received"] as const;

export const whatsappMessageSchema = z.object({
  id: uuidSchema,
  direction: z.enum(["outbound", "inbound"]),
  kind: z.string(),
  contactId: uuidSchema.nullable(),
  phone: z.string(),
  templateName: z.string().nullable(),
  category: z.string().nullable(),
  /** Readable text; authentication codes are masked. */
  bodyPreview: z.string().nullable(),
  status: z.enum(WHATSAPP_MESSAGE_STATUSES),
  errorCode: z.string().nullable(),
  errorTitle: z.string().nullable(),
  /** Thousandths of a paisa (INR). */
  costMilliMinor: z.number().int(),
  entityType: z.string().nullable(),
  entityId: uuidSchema.nullable(),
  createdAt: timestampSchema,
  sentAt: timestampSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  readAt: timestampSchema.nullable(),
  failedAt: timestampSchema.nullable(),
});
export type WhatsAppMessageDto = z.infer<typeof whatsappMessageSchema>;

/** GET /api/v1/integrations/whatsapp/messages (perm `messages:write`). */
export const listWhatsAppMessagesQuerySchema = listQuerySchema.extend({ contactId: uuidSchema.optional() });
export type ListWhatsAppMessagesQuery = z.infer<typeof listWhatsAppMessagesQuerySchema>;

/** Why one recipient of a bulk send will not get the message. */
export const WHATSAPP_BLOCK_REASONS = ["not_connected", "no_whatsapp_number", "no_opt_in", "opted_out", "template_not_approved", "missing_fact", "budget_exceeded", "contact_not_found"] as const;
export type WhatsAppBlockReason = (typeof WHATSAPP_BLOCK_REASONS)[number];

/** POST /api/v1/integrations/whatsapp/bulk/preview and …/bulk/send (perm `messages:write`). */
export const whatsappBulkInputSchema = z
  .object({
    templateId: uuidSchema,
    contactIds: z.array(uuidSchema).min(1).max(500),
    subject: whatsappSubjectSchema.nullable().optional(),
  })
  .strict();
export type WhatsAppBulkInput = z.input<typeof whatsappBulkInputSchema>;

/** The estimate shown BEFORE a bulk send (US-16.4) — and the refusal when it would breach the cap. */
export const whatsappBulkEstimateSchema = z.object({
  recipients: z.number().int(),
  eligible: z.number().int(),
  blocked: z.array(z.object({ contactId: uuidSchema, reason: z.enum(WHATSAPP_BLOCK_REASONS) })),
  estimatedCostMilliMinor: z.number().int(),
  /** Rounded up to whole paise. */
  estimatedCostMinor: z.number().int(),
  monthToDateMinor: z.number().int(),
  monthlyBudgetMinor: z.number().int(),
  remainingMinor: z.number().int(),
  withinBudget: z.boolean(),
  currency: z.literal("INR"),
});
export type WhatsAppBulkEstimateDto = z.infer<typeof whatsappBulkEstimateSchema>;

export const whatsappBulkResultSchema = z.object({
  queued: z.number().int(),
  messageIds: z.array(uuidSchema),
  estimate: whatsappBulkEstimateSchema,
});
export type WhatsAppBulkResultDto = z.infer<typeof whatsappBulkResultSchema>;

/** GET /api/v1/integrations/whatsapp/cost (perm `integrations:manage`) — the month's meter. */
export const whatsappCostMeterSchema = z.object({
  month: z.string(),
  spentMilliMinor: z.number().int(),
  spentMinor: z.number().int(),
  monthlyBudgetMinor: z.number().int(),
  remainingMinor: z.number().int(),
  messages: z.number().int(),
  byCategory: z.record(z.string(), z.object({ messages: z.number().int(), costMilliMinor: z.number().int() })),
  currency: z.literal("INR"),
});
export type WhatsAppCostMeterDto = z.infer<typeof whatsappCostMeterSchema>;

/** GET /api/v1/integrations/whatsapp/contacts/:id/window (perm `messages:write`). */
export const whatsappWindowSchema = z.object({
  contactId: uuidSchema,
  phone: z.string().nullable(),
  windowOpen: z.boolean(),
  windowExpiresAt: timestampSchema.nullable(),
  optedIn: z.boolean(),
  optedOut: z.boolean(),
});
export type WhatsAppWindowDto = z.infer<typeof whatsappWindowSchema>;

// ---------------------------------------------------------------------------
// Integrations health (US-16.1, principles §16, DX-13 — zero silent failures)
// ---------------------------------------------------------------------------

export const integrationsHealthReportSchema = z.object({
  generatedAt: timestampSchema,
  connections: z.array(
    z.object({
      connectionId: uuidSchema,
      provider: z.string(),
      displayName: z.string().nullable(),
      status: z.string(),
      health: z.enum(["pending", "healthy", "degraded", "error", "expired", "disconnected"]),
      lastOkAt: timestampSchema.nullable(),
      lastErrorAt: timestampSchema.nullable(),
      lastError: z.string().nullable(),
      failures24h: z.number().int(),
      failures7d: z.number().int(),
      /** Share of the last 7 days' calls that failed, in basis points. */
      failureRate7dBps: z.number().int(),
      pendingDeliveries: z.number().int(),
      recentFailures: z.array(
        z.object({ deliveryId: uuidSchema, operation: z.string(), at: timestampSchema, httpStatus: z.number().int().nullable(), error: z.string().nullable() }),
      ),
    }),
  ),
  webhooks: z.object({
    active: z.number().int(),
    disabled: z.number().int(),
    failing: z.number().int(),
    deadLetters24h: z.number().int(),
  }),
});
export type IntegrationsHealthReportDto = z.infer<typeof integrationsHealthReportSchema>;
